/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps, NextLayoutComponentType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Promise from 'bluebird';
import MainLayout from '@/layouts/main';
import { localeList } from '@/public/locales/locale-list';
import AppContainer from '@/components/layout/container';
import { Api } from '@/api';
import { IReadSurah } from '@/infrastructure/interfaces/i-read-surah';
import { ReadSurah } from '@/infrastructure/class/read-surah';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { routes } from '@/infrastructure/const/routes';
import { IAyat } from '@/infrastructure/interfaces/i-ayat';
import ReadSurahDetailHeader from '@/components/read-surahs/detail/header';
import AyasList from '@/components/ayas/list';
import { ITranslation } from '@/infrastructure/interfaces/i-translation';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import { IAudioSurah } from '@/infrastructure/interfaces/i-audio-surah';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import { IReciter } from '@/infrastructure/interfaces/i-reciter';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import { replaceWords } from '@/utils/helpers/replace-words';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import { getAudioSurahPath } from '@/infrastructure/functions/get-audio-surah-path';
import { getReciterSurahPath } from '@/infrastructure/functions/get-reciter-surah-path';
import { ReadSurahSlugsContext } from '@/infrastructure/context/read-surah-slugs-context';
import { addDomainForReciterImages } from '@/utils/helpers/add-domain-for-reciter-images';
import { usePlayer } from '@/infrastructure/context/player-context';
import SeoHead from '@/components/common/seo-head';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  musicRecordingMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import s from './styles.module.scss';

const api = Api.getInstance();
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;
const STATIC_PATHS_FETCH_CONCURRENCY = 10;

const isValidSlug = (slug: unknown): slug is string =>
  typeof slug === 'string' && slug.trim().length > 0;

interface IAudioSurahDetailProps {
  readSurah: IReadSurah;
  audioSurah: IAudioSurah;
  audioSurahs: IAudioSurah[];
  ayas: IAyat[];
  translations: ITranslation[];
  reciter: IReciter;
  translation: string;
}

const ReciterSurahDetailPage: NextLayoutComponentType<
  IAudioSurahDetailProps
> = (props) => {
  const {
    readSurah,
    audioSurah,
    audioSurahs,
    ayas,
    translations,
    translation,
    reciter,
  } = props;

  const { player, setTracks } = usePlayer();
  const router = useRouter();
  const { locale } = router;
  const activeTranslation = Array.isArray(router.query.translation)
    ? router.query.translation[0]
    : (router.query.translation ?? null);
  const { getSurahSlugByNumber } = useContext(ReadSurahSlugsContext);

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const { t: tMeta } = useTranslation('meta');
  const { t: tLinks } = useTranslation('links');

  const [readSurahDetail, setReadSurahDetail] = useState<ReadSurah>(
    new ReadSurah(readSurah, locale)
  );
  const [audioSurahDetail, setAudioSurahDetail] = useState<AudioSurah>(
    new AudioSurah(audioSurah, locale)
  );
  const [displayedAyas, setDisplayedAyas] = useState<IAyat[]>(ayas);

  useEffect(() => {
    if (!activeTranslation) {
      return;
    }

    (async () => {
      const { data: ayasResult } = await api.getAyasBySuraId(
        String(readSurah.id),
        activeTranslation
      );
      setDisplayedAyas(ayasResult.ayas);
    })();
  }, [activeTranslation]);

  const audiosSurahs = useMemo<AudioSurah[]>(
    () =>
      sortArrayByFieldName(
        audioSurahs.map((audioSurah) => new AudioSurah(audioSurah, locale)),
        'number'
      ),
    [audioSurahs, locale]
  );

  const canonicalPath = getReciterSurahPath(reciter.slug, readSurahDetail.slug);

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('allReciters'),
      path: routes.reciters,
    },
    {
      title: reciter.title_en,
      path: `${routes.reciters}/${reciter.slug}`,
    },
    {
      title: audioSurahDetail.titleEn,
      path: canonicalPath,
    },
  ];

  const canonicalURL = `${SITE_URL}${getLocaleForUrl(locale)}${canonicalPath}`;

  const htmlTitle = (): string => {
    return replaceWords(`${tMeta('audioSurahsDetailPageTitle')}`, {
      surahTitle: audioSurahDetail.titleEn,
      reciterTitle: reciter.title_en,
    });
  };
  const htmlDescription = (): string => {
    return replaceWords(`${tMeta('audioSurahsDetailPageDescription')}`, {
      surahTitle: audioSurahDetail.titleEn,
      reciterTitle: reciter.title_en,
    });
  };

  const schema = baseMarkupSchema([
    webPageMarkupSchema(canonicalPath, htmlTitle(), htmlDescription()),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
    musicRecordingMarkupSchema({
      audioSurah: audioSurahDetail,
      reciterName: reciter.title_en,
      reciterSlug: reciter.slug,
      url: canonicalURL,
    }),
  ]);

  useEffect(() => {
    setReadSurahDetail(new ReadSurah(readSurah, locale));
  }, [readSurah, locale]);
  useEffect(() => {
    setAudioSurahDetail(new AudioSurah(audioSurah, locale));
  }, [audioSurah, locale]);

  useEffect(() => {
    setTracks(audiosSurahs, audioSurahDetail);
  }, [audioSurahDetail, audiosSurahs]);

  useEffect(() => {
    if (player.ended && player.currentTrack) {
      router.push(
        getAudioSurahPath({
          reciterSlug: reciter.slug,
          surahNumber: player.currentTrack.number,
          getSurahSlugByNumber,
        })
      );
    }
  }, [player.ended]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  return (
    <>
      <SeoHead
        title={htmlTitle()}
        description={htmlDescription()}
        canonicalURL={canonicalURL}
        schema={schema}
      />
      <main>
        <div className={s['detail-header-wrapper']}>
          <ReadSurahDetailHeader
            readSurah={readSurahDetail}
            audioSurah={audioSurahDetail}
            audioSurahs={audiosSurahs}
            translations={translations}
            translation={activeTranslation ?? translation}
            ayasCount={ayas.length}
            reciterSlug={reciter.slug}
            className={s['detail-header']}
          />
        </div>
        <AppContainer>
          <AyasList items={displayedAyas} surah={audioSurahDetail} />
        </AppContainer>
      </main>
    </>
  );
};

ReciterSurahDetailPage.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticPaths: GetStaticPaths = async () => {
  const { data: recitersResult } = await api.getReciters(
    allRecordsPaginationOptions,
    ['slug']
  );
  const { results: reciters } = recitersResult;

  const { data: readSurahsResult } = await api.getReadSurahs(
    allRecordsPaginationOptions,
    ['slug', 'index']
  );
  const { results: readSurahs } = readSurahsResult;

  const surahSlugByNumber = new Map<number, string>(
    readSurahs
      .filter((surah: IReadSurah) => isValidSlug(surah.slug))
      .map((surah: IReadSurah): [number, string] => [surah.index, surah.slug])
  );

  const reciterSlugs = reciters
    .map((reciter) => reciter.slug)
    .filter(isValidSlug);

  interface IReciterSurahCandidate {
    reciterSlug: string;
    surahSlug: string | undefined;
  }

  const pathsByReciter = await Promise.map(
    reciterSlugs,
    async (reciterSlug): Promise<IReciterSurahCandidate[]> => {
      try {
        const { data: audioSurahsResult } =
          await api.getAudioSurahsByReciterSlug(
            reciterSlug,
            allRecordsPaginationOptions,
            ['number']
          );

        return audioSurahsResult.results.map((audioSurah: IAudioSurah) => ({
          reciterSlug,
          surahSlug: surahSlugByNumber.get(audioSurah.number),
        }));
      } catch (error) {
        console.error(
          `[getStaticPaths:/reciter/[slug]/surah/[surahSlug]] Failed to fetch reciter slug ${reciterSlug}`,
          error
        );
        return [];
      }
    },
    { concurrency: STATIC_PATHS_FETCH_CONCURRENCY }
  );

  const slugs = new Set<string>();

  const paths = pathsByReciter.flatMap((candidates) =>
    candidates.flatMap(({ reciterSlug, surahSlug }) => {
      if (!isValidSlug(surahSlug)) {
        return [];
      }

      const key = `${reciterSlug}/${surahSlug}`;

      if (slugs.has(key)) {
        return [];
      }

      slugs.add(key);
      return [{ params: { slug: reciterSlug, surahSlug } }];
    })
  );

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  try {
    const { locale = '', params } = ctx;
    const { slug: reciterSlug, surahSlug } = params || {};

    const { data: reciterResult } = await api.getReciterBySlug(
      String(reciterSlug)
    );
    const { reciter } = reciterResult;
    addDomainForReciterImages(reciter);

    const { data: readSurahResult } = await api.getReadSurahBySlug(
      String(surahSlug)
    );
    const { surah: readSurah } = readSurahResult;

    const { data: audioSurahsResult } = await api.getAudioSurahsByReciterSlug(
      reciter.slug,
      allRecordsPaginationOptions
    );
    const { results: audioSurahs } = audioSurahsResult;

    const audioSurah = audioSurahs.find(
      (surah: IAudioSurah) => surah.number === readSurah.index
    );

    if (!audioSurah) {
      return {
        notFound: true,
      };
    }

    const { data: translationsResult } = await api.getAllTranslations();
    const { translations } = translationsResult;

    const translationSlug = '';

    const { data: ayasResult } = await api.getAyasBySuraId(
      readSurah.id,
      translationSlug
    );
    const { ayas } = ayasResult;

    return {
      props: {
        readSurah,
        audioSurah,
        audioSurahs,
        ayas,
        translations,
        translation: translationSlug,
        reciter,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default ReciterSurahDetailPage;
